import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { LegalEntityStatus, LegalEntityType } from 'chaika-enums';

import { OptionType } from '@components/Select';
import { useLegalEntityOptions } from '@organization/hooks/legalEntity/useLegalEntityOptions';
import { primeLegalEntityByIdsCache } from '@organization/queries/legalEntityQueries';

export const EMPTY_IDS: string[] = [];

/** Scope props shared by the legal-entity async selects (single / multi / creatable). */
export interface LegalEntitySelectScope {
    /** Type scope for default options. @default [LegalEntityType.Clinic] */
    types?: LegalEntityType[];
    /** Status scope for default options. @default [LegalEntityStatus.Active] */
    statuses?: LegalEntityStatus[];
    /** Type scope for typed search. @default [] (unscoped) */
    searchTypes?: LegalEntityType[];
    /** Status scope for typed search. @default [] (unscoped) */
    searchStatuses?: LegalEntityStatus[];
}

interface Params extends LegalEntitySelectScope {
    selectedIds: string[];
}

interface Data {
    defaultOptions: OptionType<string>[];
    loadOptions(query: string): Promise<OptionType<string>[]>;
    isLoading: boolean;
    /** Prime the byIds cache for picked ids so cross-field reads resolve without a round-trip. No-op for []. */
    primeIds(ids: string[]): void;
}

// Shared core for the three LegalEntityAsync* selects: applies the default scope,
// runs useLegalEntityOptions, and exposes a prime helper. Each component keeps only
// its value/onChange shape on top of this.
export function useLegalEntitySelect({
    types = [LegalEntityType.Clinic],
    statuses = [LegalEntityStatus.Active],
    searchTypes = [],
    searchStatuses = [],
    selectedIds,
}: Params): Data {
    const { defaultOptions, loadOptions, isLoading } = useLegalEntityOptions({
        types,
        statuses,
        searchTypes,
        searchStatuses,
        selectedIds,
    });

    const queryClient = useQueryClient();
    const primeIds = useCallback(
        (ids: string[]): void => {
            if (ids.length > 0) {
                primeLegalEntityByIdsCache(queryClient, ids);
            }
        },
        [queryClient],
    );

    return { defaultOptions, loadOptions, isLoading, primeIds };
}
